import {
  BaseFlowBlockSchema,
  FlowBlockReturnTypeSchema,
  FlowBlockType,
  FlowProducedType,
  FlowVersionTarget,
} from 'flow-schema';
import { z } from 'zod';

import { FlowArgumentSchema } from './flowArgument.js';
import { isDefined } from '../../../../utils.js';

/**
 * Shared with skillV2
 */
export const SubFlowV2BlockConfigSchema = z
  .object({
    versionTarget: z.enum(FlowVersionTarget),
    flowId: z.string().uuid('A valid flow ID is required'),
    flowDefinitionId: z.string().uuid('A valid flow definition ID is required').optional(),
    arguments: z
      .array(FlowArgumentSchema)
      // names must be unique
      .superRefine((value, ctx) => {
        const nameIndexes = new Map<string, number>();
        const duplicateIndexes = new Set<number>();
        value.forEach(({ name }, i) => {
          if (name) {
            const priorIndex = nameIndexes.get(name);
            if (isDefined(priorIndex)) {
              duplicateIndexes.add(i);
              duplicateIndexes.add(priorIndex);
            }
            nameIndexes.set(name, i);
          }
        });
        for (const i of duplicateIndexes) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Name is duplicated',
            path: [i, 'name'],
          });
        }
      }),
  })
  .refine(
    ({ versionTarget, flowDefinitionId }) =>
      versionTarget !== FlowVersionTarget.SpecificVersion || isDefined(flowDefinitionId),
    {
      message: `A flow definition must be selected when using versionTarget=${FlowVersionTarget.SpecificVersion}`,
      path: ['flowDefinitionId'],
    }
  );

export type SubFlowV2BlockConfig = z.infer<typeof SubFlowV2BlockConfigSchema>;

/**
 * Shared with skillV2
 */
export const SubFlowV2BlockBaseSchema = z.object({
  type: z.literal(FlowBlockType.SubFlowV2),
  returnType: FlowBlockReturnTypeSchema(FlowProducedType.Any),
  config: SubFlowV2BlockConfigSchema,
});

export const SubFlowV2BlockSchema = BaseFlowBlockSchema.merge(SubFlowV2BlockBaseSchema);

export type SubFlowV2Block = z.infer<typeof SubFlowV2BlockSchema>;
